import { useState } from "react";
import { Link, FileText, CheckSquare } from "lucide-react";
import { completionBarColor } from "./chatCardUtils";
import type { DocChecklistLinksResult } from "./chatCardUtils";

const MAX_VISIBLE = 5;

interface CoverageCardProps {
  data: DocChecklistLinksResult;
}

export function CoverageCard({ data }: CoverageCardProps) {
  const [showAll, setShowAll] = useState(false);

  const coveragePercent =
    data.totalChecklistItems > 0
      ? Math.round((data.itemsWithDocuments / data.totalChecklistItems) * 100)
      : 0;

  const visible = showAll ? data.links : data.links.slice(0, MAX_VISIBLE);
  const remaining = data.links.length - MAX_VISIBLE;

  return (
    <div className="mt-2 space-y-2">
      {/* Header */}
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted uppercase tracking-wider">
        <Link size={12} />
        <span>Document Coverage</span>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-3 text-[11px]">
        <span className="text-emerald-500 font-medium">
          {data.itemsWithDocuments} covered
        </span>
        <span className="text-amber-500 font-medium">
          {data.itemsWithoutDocuments} uncovered
        </span>
        <span className="text-muted">
          {data.totalLinks} link{data.totalLinks !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Coverage bar */}
      <div className="w-full h-2 rounded-full bg-border/30 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${completionBarColor(coveragePercent)}`}
          style={{ width: `${coveragePercent}%` }}
        />
      </div>

      {/* Link list */}
      {visible.length > 0 && (
        <div className="space-y-1">
          {visible.map((link, i) => (
            <div
              key={`${link.documentId}-${link.checklistItemId}-${i}`}
              className="flex items-center gap-2 rounded-md bg-card px-2 py-1.5 text-[11px]"
            >
              <FileText size={10} className="text-blue-400 shrink-0" />
              <span className="text-primary truncate font-medium">
                {link.documentName}
              </span>
              <span className="text-muted shrink-0 mx-0.5">&rarr;</span>
              <CheckSquare size={10} className="text-emerald-400 shrink-0" />
              <span className="text-muted truncate">
                {link.itemDescription}
              </span>
            </div>
          ))}
        </div>
      )}

      {remaining > 0 && !showAll && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="text-xs text-accent hover:text-accent/80 transition-colors font-medium"
        >
          +{remaining} more links
        </button>
      )}
    </div>
  );
}
